import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { ActionDialog } from '@/common/dialog/ActionDialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityBanner } from '@/common/entityFramework/entityBanner/EntityBanner';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useTab } from '@/common/entityFramework/entityTabs/TabContext';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { ContentBox } from '@/common/layout/ContentBox';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AbvOpenPeriodToggleButton } from './AbvOpenPeriodToggleButton';

export const AbvManAccrAdjList = () => {
  const rsrc = useRsrc();

  const module = useModuleFromRoute();
  const entityListRef = useRef();

  const { openDialog } = useDialog();

  // NavState
  const { navState, assign } = useNavState();
  const { tabId } = useTab();
  const manAccrAdjStateId = tabId ? tabId + 'ManAccrAdjState' : 'ManAccrAdjState';
  const manAccrAdjState = navState[manAccrAdjStateId] ?? {};

  const [paidPeriodFlag, setPaidPeriodFlag] = useState(manAccrAdjState.paidPeriodFlag ?? true);

  const entity = 'MAN_ACCR_ADJ';

  // ----- NavState -----
  const saveNavState = () => {
    const newManAccrAdjState = {
      paidPeriodFlag,
    };

    assign(manAccrAdjStateId, newManAccrAdjState);
  };

  useEffect(() => {
    saveNavState();
  }, [paidPeriodFlag]);

  // ----- Data -----
  const clientSideRowData = useCallback(
    (setDataSource) => {
      const dataPlugin = navState.pluginCode ?? 'MAN_ACCR_ADJ_OPEN_PERIOD_DATA_PLUGIN';
      const reqParam = {
        ...navState.reqParam,
        entity,
        paidPeriodFlag,
      };

      rsrc.data(module, dataPlugin, reqParam, entity).then((res) => {
        setDataSource(res);
        totalsRow();

        // Apply initial filters from navState if present
        if (navState.listState?.filters) {
          setTimeout(() => {
            const gridApi = entityListRef.current?.gridRef?.current?.gridApiRef?.current;
            if (!gridApi) return;
            const fieldDefs = entityListRef.current?.fieldDefs ?? [];
            const filterModel = {};

            Object.entries(navState.listState.filters).forEach(([key, value]) => {
              const fieldDef = fieldDefs.find((fd) => fd.field === key);
              if (fieldDef?.filter === 'agSetColumnFilter') {
                filterModel[key] = {
                  filterType: 'set',
                  values: Array.isArray(value) ? value : [value],
                };
              } else {
                filterModel[key] = {
                  filter: value,
                  filterType: 'text',
                  type: 'contains',
                };
              }
            });

            gridApi.setFilterModel(filterModel);
          }, 0);
        }
      });
    },
    [paidPeriodFlag],
  );

  const totalsRow = () => {
    const gridRef = entityListRef.current.gridRef;
    if (!paidPeriodFlag) {
      gridRef.current.setTotalsRow(null);
      return;
    }

    const totals = {
      code: 'TOTAL',
      amt: 0,
    };

    const api = gridRef.current.gridApiRef.current;
    api.forEachNodeAfterFilterAndSort((node) => {
      totals.amt = totals.amt + node.data.amt;
    });
    totals.amt = Math.round(totals.amt * 100) / 100;

    gridRef.current.setTotalsRow(totals);
  };

  const onFilterChanged = () => {
    totalsRow();
  };

  // ----- Actions -----
  const onBulkAdj = () => {
    const api = entityListRef.current.gridRef.current.gridApiRef.current;
    const fieldDefs = entityListRef.current.fieldDefs;

    const expr = ExprUtils.filterModelToExpr(api.getFilterModel(), fieldDefs);

    actionDialog({ addlReqData: { expr } }, 'Bulk Adjustment', 'BULK_ADJ_MAN_ACCR_ADJ_ACTION_PLUGIN');
  };

  const augButtons = useCallback(
    (buttons) => {
      // TODO: Remove from Entity List in general ??
      const exportOnly = buttons.filter((btn) => btn.type === ExportButton);
      buttons.splice(0, buttons.length, ...exportOnly);

      // Do not render any other button if routed from another Entity
      if (!navState.pluginCode) {
        if (paidPeriodFlag) {
          buttons.push(
            <TitleButton
              toolTip={'Bulk Adjustment'}
              icon={fas.faCopy}
              dataTestId="bulk-adj"
              onClick={onBulkAdj}
            ></TitleButton>,
          );
        }

        buttons.push(
          <AbvOpenPeriodToggleButton paidPeriodFlag={paidPeriodFlag} setPaidPeriodFlag={setPaidPeriodFlag} />,
        );
      }
    },
    [paidPeriodFlag],
  );

  const getContextMenuItems = (params) => {
    let contextMenus = entityListRef.current.getContextMenuItems(params);

    // Do not allow to run an Action on when routed from another Entity
    if (!navState.pluginCode) {
      // Splitting
      let splittingData = {
        entityId: params.node.data.id,
      };

      contextMenus.push({
        name: 'Splitting',
        action: () => actionDialog(splittingData, 'Splitting', 'SPLIT_MAN_ACCR_ADJ_ACTION_PLUGIN'),
      });
    }

    return contextMenus;
  };

  const actionDialog = (data, title, pluginCode) => {
    openDialog(ActionDialog, {
      data: {
        ...data,
        title,
        pluginCode,
        entity,
      },
    });
  };

  const exportReq = useCallback(() => {
    const dataPluginCode = navState.pluginCode ?? 'MAN_ACCR_ADJ_OPEN_PERIOD_DATA_PLUGIN';
    const reqParam = {
      ...navState.reqParam,
      entity,
      paidPeriodFlag,
    };

    return {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: 'Manual Accrual Adjustments',
      module,
      entity,
      uiType: 'MAN_ACCR_ADJ_LIST',
      dataPluginCode,
      reqParam,
      fieldDefs: entityListRef.current?.fieldDefs ?? [],
    };
  }, [entity, module, navState.pluginCode, navState.reqParam, paidPeriodFlag]);

  const buttons = [<ExportButton key="export" entityName="Manual Accrual Adjustments" exportReq={exportReq} />];
  if (!navState.pluginCode) {
    if (paidPeriodFlag) {
      buttons.push(
        <TitleButton
          key="bulk-adj"
          toolTip={'Bulk Adjustment'}
          icon={fas.faCopy}
          dataTestId="bulk-adj"
          onClick={onBulkAdj}
        ></TitleButton>,
      );
    }

    buttons.push(
      <AbvOpenPeriodToggleButton
        key="open-period-toggle"
        paidPeriodFlag={paidPeriodFlag}
        setPaidPeriodFlag={setPaidPeriodFlag}
      />,
    );
  }

  return (
    <ContentBox>
      <Title icon={fas.faPencil} title="Manual Accrual Adjustments" buttons={buttons} />
      <EntityBanner />
      <EntityList
        ref={entityListRef}
        entity={entity}
        clientSideRowData={clientSideRowData}
        showTitle={false}
        augButtons={augButtons}
        gridOptions={{
          getContextMenuItems,
        }}
        events={{
          onFilterChanged,
        }}
      />
    </ContentBox>
  );
};
